
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { RouteNames } from '@/enums/routes/RouteNames';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import Project from '@/models/Project';
import ProjectRepository from '@/repositories/ProjectRepository';
import Client from '@/models/Client';
import { ProductCategories } from '@/enums/global/ProductCategories';
import { AxiosResponse } from 'axios';
import { AvailableClientFields } from '@/interfaces/components/clients/AvailableClientFields';
import { UserRightsMixin } from '@/mixins/UserRights';
import { mixins } from 'vue-class-component';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { PopupEventData } from '@/interfaces/components/PopupEventData';
import { PopupEvents } from '@/enums/global/PopupEvents';
import HelpPage from '@/shared/components/HelpPage.vue';
import { ErrorOptions } from '@/interfaces/ErrorOptions';
import BlockedTag from '@/components/global/BlockedTag.vue';

@Component({
    name: 'ClientTopBar',
    components: {
        HelpPage,
        BlockedTag
    },
})
export default class ClientTopBar extends mixins<UserRightsMixin>(UserRightsMixin) {
    @Prop({ required: true }) private client!: Client;
    @Prop({ required: true }) private clientId!: string | null;
    @Prop({ required: false }) private projectId!: string | null;
    @Prop({ required: true }) private isClientExisting!: boolean;
    @Prop({ required: true }) private isSaveButtonDisabled!: boolean;
    private loadingOverlay = new LoadingOverlayHelper(this, {});
    private debounceTimer: number | null = null;

    private get productType(): string {
        return this.$route.path.includes('client') ? ProductCategories.customers : ProductCategories.projects;
    }
    private get productName(): string | null {
        return this.$route.path.includes('client') ? this.client.name : '';
    }
    private get returnRoute(): string {
        return this.$route.path.includes('client') ? RouteNames.client : RouteNames.project;
    }

    private get isClientCompany(): boolean {
        return this.client?.isCompany;
    }

    private goBack(): void {
        this.$router.push({ name: this.returnRoute });
    }

    private onSaveClient() {
        EventBus.$emit(EventBusEvents.updateClient);
    }

    private sendToIngenious(): void {
        if (this.debounceTimer) {
            window.clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = window.setTimeout(() => {
            EventBus.$emit(EventBusEvents.updateClient, { sendToIngenious: true });
            this.debounceTimer = null;
        }, 400);
    }

    private toggleBlockClient() {
        if (this.client.roltekAppPartner) {
            this.$notification.warning({
                message: this.$t('Action Restricted') as string,
                description: this.$t('You cannot block a Roltek partner client.') as string,
            });
            return;
        }

        const willBlock = !this.client.isBlocked;
        const popupData: PopupEventData = {
            popupEvent: PopupEvents.toggleBlockClient,
            data: {
                clientName: this.client.name,
                block: willBlock,
                confirmAction: (reason: string) => this.executeToggleBlock(willBlock, reason),
            },
        };

        EventBus.$emit(EventBusEvents.onToggleBlockClient, popupData);
    }

    private async executeToggleBlock(block: boolean, reason: string = '') {
        this.loadingOverlay.start();
        try {
            if (this.clientId) {
                const res = await Client.toggleBlockClient(this.clientId, block, reason);
                Vue.set(this.client, 'isBlocked', block);
                Vue.set(this.client, 'clientLogs', res.clientLogs ?? []);

                this.$notification.success({
                    message: block
                        ? this.$t('Client blocked and synced with Ingenious!') as string
                        : this.$t('Client successfully unblocked!') as string,
                    description: '',
                });

                EventBus.$emit(EventBusEvents.closePopup);
                EventBus.$emit(EventBusEvents.runEntitySearch);
            }
        } catch (e) {
            const error = e as ErrorOptions;
            this.$notification.error({
                message: this.$t('There was an error') as string,
                description: error.response ? error.response.data.meta.message : error.message,
            });
        } finally {
            this.loadingOverlay.stop();
        }
    }

    private async onDeleteClient() {
        const popupData: PopupEventData = {
            popupEvent: PopupEvents.deleteClientPerson,
            data: {
                clientId: this.clientId,
                client: this.client,
                deleteClient: this.deleteClientAndClosePopup.bind(this),
            },
        };

        EventBus.$emit(EventBusEvents.onDeleteClientPerson, popupData);
    }

    private async deleteClientAndClosePopup(params: any) {
        const clientId = params.clientId as string;
        const projects = params.projects as Project[];
        const newClientId = params.newClientId ? (params.newClientId as string) : null;
        const newContactPersonId = params.newContactPersonId ? (params.newContactPersonId as string) : null;

        this.loadingOverlay.start();
        try {
            if (projects.length && newClientId && newContactPersonId) {
                // tslint:disable
                const updateCalls: Promise<AxiosResponse<any>>[] = [];

                projects.forEach((project: Project) => {
                    updateCalls.push(
                        Project.updateClientAndContactPerson({
                            projectId: project.id,
                            clientId: newClientId,
                            contactPersonId: newContactPersonId,
                        }),
                    );
                });

                await Promise.all(updateCalls);
            }

            await this.deleteClient(clientId);
        } catch (e) {
            const error = e as ErrorOptions;

            this.$notification.error({
                message: this.$t('There was an error') as string,
                description: error.response ? error.response.data.meta.message : error.message,
            });
            this.loadingOverlay.stop();
            return;
        }
        this.$notification.success({
            message: this.$t('Client successfully deleted!') as string,
            description: '',
            class: 'client-delete-success',
        });

        EventBus.$emit(EventBusEvents.closePopup);

        this.$router.push({ name: RouteNames.client });
        this.loadingOverlay.stop();

        return Promise.resolve();
    }

    private async deleteClient(clientId: string) {
        try {
            await Client.deleteExisting(clientId);
        } catch (error) {
            return Promise.reject(error);
        }
        return Promise.resolve();
    }

    private getClientInformation() {
        EventBus.$emit(EventBusEvents.getClientInformation);
    }
}
